import { useMemo, useState } from 'react';
import useOrderFlow from '../../../../hooks/order/useOrderFlow';
import useOrderPreparationTime from '../../../../hooks/order/useOrderPreparationTime';
import useAppMethods from '../../../../hooks/utility/useAppMethods';
import { ORDER_METHODS, REDUX_STORE_SLICES } from '../../../../services/exports/Constants';
import useOrderMethodsStatus from '../../../../hooks/availability/useOrderMethodsStatus';
import collect from 'collect.js';
import moment from 'moment';
import useCompanyLocation from '../../../../hooks/location/useCompanyLocation';
import useOpeningStatus from '../../../../hooks/availability/useOpeningStatus';
import useStore from '../../../../hooks/store/useStore';
import useTheme from '../../../../hooks/ui/useTheme';
import AddressHelpers from '../../../../services/helpers/AddressHelpers';
import HelperMethods from '../../../../services/exports/HelperMethods';
import { useTranslation } from 'react-i18next';
import ClockIcon from '../../../../assets/icons/order-methods/ClockIcon';
import CompanyDetailsModal from '../../../../modals/CompanyDetailsModal';
import OrderMethodsTabBar from '../../../../components-library/navigation/OrderMethodsTabBar';
import LocationIcon from '../../../../assets/icons/LocationIcon';
import BackIcon from '../../../../assets/logo/BackIcon';
import classnames from 'classnames';
import { CompanyDetailsInterface } from '../../../../services/exports/Interfaces';
import useScreenType from '../../../../hooks/utility/useScreenType';
import useInitialData from '../../../../hooks/global/useInitialData';
import classNames from 'classnames';

interface Props {
  className?: string;
  type?: 1 | 2;
}

interface ComponentProps extends Props {
  openOrderMethodModal: () => void;
  address: string;
  company: CompanyDetailsInterface;
  renderPreparationTime: (method: ORDER_METHODS) => string;
  toggleCompanyDetailsModal: () => void;
}

const OrderMethod = ({ className, type }: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Storefront:Components:Orders:OrderMethod' });
  const { localData } = useOrderFlow();
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const { isDesktop } = useScreenType();
  const getPreparationTime = useOrderPreparationTime(company);
  const { formatDateTime, createScheduledOrderSlot, toggleOrderMethodModal } = useAppMethods();
  const { isOrderMethodCurrentlyOpen, isOrderMethodOpenForFutureOrders } = useOrderMethodsStatus();
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState<boolean>(false);
  const location = useCompanyLocation(localData.scheduled_for === null ? null : localData.scheduled_for);

  const openOrderMethodModal = () => toggleOrderMethodModal({ openModal: true, orderMethod: localData.method });
  const toggleCompanyDetailsModal = () => setShowCompanyDetailsModal(!showCompanyDetailsModal);

  const getAddress = (): string => {
    if (localData.method === ORDER_METHODS.DELIVERY) {
      return AddressHelpers.setCountry(location?.country).format({
        street_name: localData?.delivery_street_name,
        city: localData?.delivery_city,
        zip_code: localData?.delivery_zip_code,
      });
    }

    return AddressHelpers.setCountry(location?.country).format({
      street_name: location?.address,
      city: location?.city,
      zip_code: location?.zip_code,
    });
  };

  const getScheduledTime = (): string => {
    if (localData.scheduled_for === null) {
      return t('asap');
    }

    return moment(localData.scheduled_for).isSame(moment(), 'day')
      ? createScheduledOrderSlot(localData.scheduled_for)
      : formatDateTime(localData.scheduled_for, { format: 'dd, DD MMM HH:mm' });
  };

  const renderPreparationTime = (method: ORDER_METHODS) => {
    const isMethodAvailable = isOrderMethodCurrentlyOpen(method) || isOrderMethodOpenForFutureOrders(method);

    if (!isMethodAvailable) {
      return t('unavailable');
    }

    if (localData.scheduled_for) {
      return getScheduledTime();
    }

    return t('ready_in_time', {
      time: HelperMethods.formatTimeInterval(getPreparationTime(method)),
    });
  };

  const renderCompanyDetailsModal = useMemo<JSX.Element>(() => {
    return (
      <CompanyDetailsModal
        data={company}
        openModal={showCompanyDetailsModal}
        toggleModal={() => setShowCompanyDetailsModal(false)}
      />
    );
  }, [company, showCompanyDetailsModal]);

  const config: ComponentProps = {
    type,
    className,
    company,
    address: getAddress(),
    renderPreparationTime,
    openOrderMethodModal,
    toggleCompanyDetailsModal,
  };

  return (
    <>
      {isDesktop ? <OrderMethod.Desktop {...config} /> : <OrderMethod.Mobile {...config} />}
      {renderCompanyDetailsModal}
    </>
  );
};

export default OrderMethod;

OrderMethod.Desktop = (props: ComponentProps) => {
  return (
    <div className={classnames('flex flex-col gap-md', props?.className)}>
      <OrderMethod.Card {...props} />
      <div className="flex lg:flex-row flex-col gap-md w-fit">
        <OrderMethod.TabBar />
        <OrderMethod.Location {...props} />
      </div>
    </div>
  );
};

OrderMethod.Mobile = (props: ComponentProps) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Storefront:Components:Orders:OrderMethod' });
  const { className, company } = props;
  if (props?.type === 2) {
    return (
      <div className={classnames('flex flex-col items-center gap-md px-md', className)}>
        <div className="w-full flex flex-col gap-xs items-center">
          <OrderMethod.OpeningHours {...props} />
        </div>
        <div className="flex flex-col gap-md w-full">
          <OrderMethod.Time {...props} />
          <OrderMethod.Location {...props} />
        </div>
      </div>
    );
  }
  return (
    <div className={classnames('flex flex-col items-center gap-md px-md', className)}>
      <div className="w-full flex flex-col gap-xs items-center">
        <h4 className="text-center font-semibold text-[18px]">{t('menu', { name: company?.name })}</h4>
        <OrderMethod.OpeningHours {...props} />
      </div>
      <div className="flex flex-col gap-md w-full">
        <OrderMethod.TabBar />
        <OrderMethod.Time {...props} />
        <OrderMethod.Location {...props} />
      </div>
    </div>
  );
};

OrderMethod.TabBar = () => {
  const { localData } = useOrderFlow();
  return (
    <div className="w-full lg:min-w-[400px]">
      <OrderMethodsTabBar
        orderMethod={localData?.method}
        className="!rounded-[8px] !p-none h-[40px] bg-gray-600 dark:!bg-brand-card"
        tabBarItemClassName="!h-[40px] !p-none"
        tabBarItemActiveClassName="!rounded-[8px] !h-[40px] !border shadow-section dark:shadow-none !border-gray-1000 dark:!border-brand-border"
      />
    </div>
  );
};

OrderMethod.Card = ({
  company,
  address,
  renderPreparationTime,
  openOrderMethodModal,
  toggleCompanyDetailsModal,
}: ComponentProps) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Storefront:Components:Orders:OrderMethod' });
  const { data } = useInitialData();
  const { franchise } = data;
  const { localData } = useOrderFlow();
  const theme = useTheme();
  const openingStatus = useOpeningStatus();
  const companyPicture = collect(company?.pictures).first();

  return (
    <div className="border border-gray-1000 dark:border-brand-border bg-brand-card px-lg py-md rounded-[8px] min-w-[590px] w-fit flex justify-between gap-md">
      <div className="flex flex-col gap-md shrink-0">
        <div className="flex flex-col gap-[4px]">
          <h4 className="text-md font-semibold">{t('menu', { name: company?.name })}</h4>
          <div className="flex items-center gap-x-sm">
            <p className="text-sm font-light">{address}</p>
            {franchise?.has_multiple_companies && (
              <button className="underline text-sm font-medium h-fit" onClick={openOrderMethodModal}>
                {t('buttons.change_location')}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center h-fit gap-x-xs">
          <ClockIcon className="w-[20px] h-[20px]" color={theme.color.brand.text.primary} />
          {!localData?.scheduled_for && (
            <>
              <h6 className="text-sm">ASAP</h6>
              <h6 className="text-sm">|</h6>
            </>
          )}
          <p className="font-medium text-sm">{renderPreparationTime(localData.method)}</p>
          <button className="underline text-sm font-medium h-fit" onClick={openOrderMethodModal}>
            {t('buttons.change_time')}
          </button>
        </div>
        <div className="flex h-fit gap-x-sm items-center">
          <h6
            className={classNames('text-sm text-primary', {
              '!text-brand-text-secondary': !openingStatus.is_open,
            })}
          >
            {openingStatus.details_text}
          </h6>
          <button
            className={classNames('underline text-sm font-medium text-primary', {
              '!text-brand-text-secondary': !openingStatus.is_open,
            })}
            onClick={toggleCompanyDetailsModal}
          >
            {t('buttons.see_hours')}
          </button>
        </div>
      </div>
      {companyPicture && (
        <img
          src={companyPicture}
          alt={company?.name}
          className="w-[203px] h-[141px] overflow-hidden object-cover rounded-lg flex-none"
        />
      )}
    </div>
  );
};

OrderMethod.Location = ({ openOrderMethodModal, address }: ComponentProps) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Storefront:Components:Orders:OrderMethod' });
  const theme = useTheme();
  const { localData } = useOrderFlow();
  return (
    <button
      className="flex gap-2 items-center h-[38px] justify-between p-sm border border-gray-1000 dark:border-brand-border rounded-[8px] w-full bg-brand-card"
      onClick={openOrderMethodModal}
    >
      <div className="flex items-center gap-2">
        <LocationIcon className="w-4 h-4" color={theme.color.brand.text.primary} />
        <h6 className="text-sm text-left line-clamp-1">{t(`locations.${localData.method}`, { address })}</h6>
      </div>
      <BackIcon className="w-3 h-3 rotate-90" type={2} color={theme.color.brand.text.primary} />
    </button>
  );
};

OrderMethod.Time = ({ openOrderMethodModal, renderPreparationTime }: ComponentProps) => {
  const { localData } = useOrderFlow();
  const theme = useTheme();
  return (
    <button
      className="flex gap-2 items-center justify-between h-[38px] p-sm border border-gray-1000 dark:border-brand-border rounded-[8px] w-full bg-brand-card"
      onClick={openOrderMethodModal}
    >
      <div className="flex items-center gap-2">
        <ClockIcon className="w-4 h-4" color={theme.color.brand.text.primary} />
        <h6 className="text-sm text-left line-clamp-1">{renderPreparationTime(localData.method)}</h6>
      </div>
      <BackIcon className="w-3 h-3 rotate-90" type={2} color={theme.color.brand.text.primary} />
    </button>
  );
};

OrderMethod.OpeningHours = ({ toggleCompanyDetailsModal }: ComponentProps) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Storefront:Components:Orders:OrderMethod' });
  const openingStatus = useOpeningStatus();
  return (
    <div className="flex divide-x divide-gray text-sm">
      <div className="flex justify-between items-center gap-x-sm px-xs">
        <h6 className="text-sm text-primary text-center">{openingStatus.details_text}</h6>
        <button className="underline text-sm font-medium text-primary" onClick={toggleCompanyDetailsModal}>
          {t('buttons.see_hours')}
        </button>
      </div>
    </div>
  );
};
